﻿import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  alterarArea,
  alternarArea,
  criarArea,
  listarAreas,
  type DadosDeArea,
} from '@/features/cadastros/api/cadastrosApi';
import { chaveDeReferencias } from '@/features/referencias/useReferencias';
import { descreverErro } from '@/lib/erros';
import type { AreaNaLista, TipoArea } from '@/types/api';

import estilos from './Administracao.module.css';

/**
 * Cadastro de areas.
 *
 * NAO EXISTE EXCLUISAO, so inativacao: area e referenciada por acao, por historico e por
 * impedimento, e apagar deixaria a trilha de auditoria com lacuna.
 *
 * O TIPO DA AREA DECIDE QUAL PAINEL MOSTRA A ACAO, e por isso a tela explica cada opcao
 * em vez de so listar o nome do enum. Criar área com o tipo errado faz a acao aparecer no
 * painel errado, e o sintoma seria "minhas acoes desapareceram".
 */

const chaveDeAreas = ['areas'] as const;

const tipos: { valor: TipoArea; rotulo: string; explicacao: string }[] = [
  { valor: 'Cs', rotulo: 'CS', explicacao: 'Origina solicitações. Aparece no Painel CS' },
  {
    valor: 'Produto',
    rotulo: 'Produto',
    explicacao: 'Analisa, aprova e executa. Aparece no Painel Produto',
  },
  {
    valor: 'Apoio',
    rotulo: 'Apoio',
    explicacao: 'Executa ações direcionadas a si. COE, Suporte, FAP Clínicas',
  },
  { valor: 'Externo', rotulo: 'Externo', explicacao: 'Fora da empresa. Exemplo: LG' },
];

export function PaginaDeAreas() {
  const queryClient = useQueryClient();

  const { data, error, isPending } = useQuery({
    queryKey: chaveDeAreas,
    queryFn: ({ signal }) => listarAreas(signal),
  });

  const [emEdicao, definirEmEdicao] = useState<number | null>(null);
  const [nome, definirNome] = useState('');
  const [tipo, definirTipo] = useState<TipoArea>('Cs');
  const [ordem, definirOrdem] = useState(0);
  const [falha, definirFalha] = useState<string | null>(null);

  /**
   * Invalida areas E referencias.
   *
   * O cache de referencias tem validade longa e alimenta as telas de acao. Sem
   * invalidar, uma area recem-criada nao apareceria no seletor de nova acao por meia
   * hora, e pareceria que o cadastro nao funcionou.
   */
  const recarregar = async () => {
    await queryClient.invalidateQueries({ queryKey: chaveDeAreas });
    await queryClient.invalidateQueries({ queryKey: chaveDeReferencias });
  };

  const limpar = () => {
    definirEmEdicao(null);
    definirNome('');
    definirTipo('Cs');
    definirOrdem(0);
  };

  const salvar = useMutation({
    mutationFn: (dados: DadosDeArea) =>
      emEdicao === null ? criarArea(dados) : alterarArea(emEdicao, dados),
    onSuccess: async () => {
      definirFalha(null);
      limpar();
      await recarregar();
    },
    onError: (erro) => {
      definirFalha(descreverErro(erro));
    },
  });

  const alternar = useMutation({
    mutationFn: ({ id, ativar }: { id: number; ativar: boolean }) => alternarArea(id, ativar),
    onSuccess: async () => {
      definirFalha(null);
      await recarregar();
    },
    onError: (erro) => {
      definirFalha(descreverErro(erro));
    },
  });

  const editar = (area: AreaNaLista) => {
    definirEmEdicao(area.id);
    definirNome(area.nome);
    definirTipo(area.tipo);
    definirOrdem(area.ordem);
  };

  const inativar = (area: AreaNaLista) => {
    /*
      O aviso usa a contagem que o SERVIDOR devolveu. A operacao nao e bloqueada: pode
      ser legitimo inativar uma area que ainda tem trabalho em andamento. O que nao pode
      e a pessoa descobrir depois.
    */
    const aviso =
      area.acoesVinculadas > 0
        ? `${String(area.acoesVinculadas)} ${area.acoesVinculadas === 1 ? 'ação' : 'ações'} ` +
          `${area.acoesVinculadas === 1 ? 'aponta' : 'apontam'} para "${area.nome}". ` +
          'Elas continuam funcionando, e a area deixa de aparecer em cadastro novo. Inativar?'
        : `Inativar a area "${area.nome}"?`;

    if (window.confirm(aviso)) {
      alternar.mutate({ id: area.id, ativar: false });
    }
  };

  const ocupado = salvar.isPending || alternar.isPending;

  return (
    <section className={estilos.pagina}>
      <div>
        <h2 className={estilos.titulo}>Áreas</h2>
        <p className={estilos.apoio}>
          Área é escopo de dados, não papel. O tipo decide em qual painel a ação aparece.
        </p>
      </div>

      {falha !== null && (
        <p className={estilos.falha} role="alert">
          {falha}
        </p>
      )}

      <section className={estilos.cartao}>
        <h2 className={estilos.secao}>{emEdicao === null ? 'Nova área' : 'Editar área'}</h2>

        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            salvar.mutate({ nome, tipo, ordem });
          }}
        >
          <div className={estilos.linha}>
            <div className={estilos.campo}>
              <label className={estilos.rotulo} htmlFor="nome">
                Nome
              </label>
              <input
                id="nome"
                className={estilos.entrada}
                value={nome}
                required
                onChange={(evento) => {
                  definirNome(evento.target.value);
                }}
              />
            </div>

            <div className={estilos.campo}>
              <label className={estilos.rotulo} htmlFor="tipo">
                Tipo
              </label>
              <select
                id="tipo"
                className={estilos.selecao}
                value={tipo}
                onChange={(evento) => {
                  definirTipo(evento.target.value as TipoArea);
                }}
              >
                {tipos.map((opcao) => (
                  <option key={opcao.valor} value={opcao.valor}>
                    {opcao.rotulo}
                  </option>
                ))}
              </select>
              <span className={estilos.ajuda}>
                {tipos.find((t) => t.valor === tipo)?.explicacao}
              </span>
            </div>

            <div className={`${estilos.campo} ${estilos.campoEstreito}`}>
              <label className={estilos.rotulo} htmlFor="ordem">
                Ordem
              </label>
              <input
                id="ordem"
                className={estilos.entrada}
                type="number"
                value={ordem}
                onChange={(evento) => {
                  definirOrdem(Number(evento.target.value));
                }}
              />
            </div>
          </div>

          {/* Fora da grade dos campos: dentro dela, a auto-colocacao empurrava o primeiro
              campo para a coluna 2. Ver o comentario em Administracao.module.css. */}
          <div className={estilos.acoes}>
            {emEdicao !== null && (
              <button className={estilos.botaoSecundario} type="button" onClick={limpar}>
                Desistir
              </button>
            )}

            <button className={estilos.botaoPrimario} type="submit" disabled={ocupado}>
              {emEdicao === null ? 'Criar área' : 'Salvar'}
            </button>
          </div>
        </form>
      </section>

      <div className={estilos.moldura}>
        <table className={estilos.tabela}>
          <caption className="apenas-leitor-de-tela">Áreas cadastradas</caption>
          <thead>
            <tr>
              <th scope="col">Nome</th>
              <th scope="col">Tipo</th>
              <th scope="col">Ordem</th>
              <th scope="col">Ações vinculadas</th>
              <th scope="col">Situação</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {data?.map((area) => (
              <tr key={area.id}>
                <td>{area.nome}</td>
                <td>{tipos.find((t) => t.valor === area.tipo)?.rotulo ?? area.tipo}</td>
                <td>{String(area.ordem)}</td>
                <td>{String(area.acoesVinculadas)}</td>
                <td>
                  <span className={area.ativa ? estilos.seloAtivo : estilos.seloInativo}>
                    {area.ativa ? 'Ativa' : 'Inativa'}
                  </span>
                </td>
                <td>
                  <div className={estilos.acoesDaLinha}>
                    <button
                      className={estilos.botaoSecundario}
                      type="button"
                      disabled={ocupado}
                      onClick={() => {
                        editar(area);
                      }}
                    >
                      Editar
                    </button>
                    {area.ativa ? (
                      <button
                        className={estilos.botaoAtencao}
                        type="button"
                        disabled={ocupado}
                        onClick={() => {
                          inativar(area);
                        }}
                      >
                        Inativar
                      </button>
                    ) : (
                      <button
                        className={estilos.botaoSecundario}
                        type="button"
                        disabled={ocupado}
                        onClick={() => {
                          alternar.mutate({ id: area.id, ativar: true });
                        }}
                      >
                        Reativar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {isPending && <p className={estilos.aviso}>Carregando áreas...</p>}

        {error !== null && (
          <p className={estilos.aviso} role="alert">
            {descreverErro(error)}
          </p>
        )}

        {data !== undefined && data.length === 0 && (
          <p className={estilos.aviso}>
            Nenhuma área cadastrada. Sem área não é possível criar ação: ela é obrigatória como
            responsavel.
          </p>
        )}
      </div>
    </section>
  );
}
